<script>
    import { toast } from "@zerodevx/svelte-toast";
    import {
        AccordionItem,
        Button,
        Card,
        Checkbox,
        Dropdown,
        DropdownItem,
        Modal,
        P,
        Span,
        Spinner,
    } from "flowbite-svelte";
    import {
        ChevronDownOutline,
        StopOutline,
        TrashBinOutline,
    } from "flowbite-svelte-icons";
    import { onDestroy } from "svelte";
    import { make_request } from "../../service/http_calls";
    import {
        deleteNestedDict,
        getNestedDict,
        updateNestedDict,
    } from "../../service/sse";
    import { createLocalStorageStore, global_state } from "../../service/store";
    import CustomAccordion from "../components/CustomAccordion.svelte";
    import GenericConfirmModal from "../components/GenericConfirmModal.svelte";
    import RealtimeAnalysis from "./RealtimeAnalysis.svelte";
    import RealtimeTranscription from "./RealtimeTranscription.svelte";

    let leftPanelVisible = true;
    const togglePanel = () => (leftPanelVisible = !leftPanelVisible);

    let projects = [];

    // Modal state
    let downloadModalOpen = false;
    let fullTranscript = true;

    // Download options for each project
    const downloadOptions = createLocalStorageStore("download_options", {});

    let download_status = null;
    let modal_open = false;

    // Force reactivity key
    $: reactivityKey = JSON.stringify($downloadOptions);

    // Automatically uncheck review if draft is not checked
    $: if ($downloadOptions) {
        Object.keys($downloadOptions).forEach((projectId) => {
            if (
                $downloadOptions[projectId] &&
                !$downloadOptions[projectId].draft &&
                $downloadOptions[projectId].review
            ) {
                $downloadOptions[projectId].review = false;
            }
        });
    }

    global_state.subscribe((s) => {
        const data = getNestedDict(s, ["realtime", "download", "data"]);
        if (data?.base64_file && data?.file_type) {
            toast.push("Downloading...");
            const byteArray = Uint8Array.from(atob(data.base64_file), (c) =>
                c.charCodeAt(0),
            );
            const blob = new Blob([byteArray], {
                type:
                    data.file_type === "docx"
                        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        : data.file_type,
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const today = new Date();
            const formattedDate = today
                .toISOString()
                .slice(0, 10)
                .replace(/-/g, "");
            a.download = `${formattedDate}_${data.file_name}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            updateNestedDict(s, ["realtime", "download", "data"], {
                completed: "true",
                base64_file: null,
                file_name: null,
                file_type: null,
            });
        }

        const status = getNestedDict(s, [
            "realtime",
            "download",
            "data",
            "status",
        ]);
        if (status == "") {
            download_status = null;
        } else {
            download_status = status;
        }
    });

    const unsubscribe = global_state.subscribe(async (state) => {
        try {
            const raw = getNestedDict(state, [
                "realtime",
                "router",
                "projects",
                "data",
            ]);
            const newProjects = raw || {};
            if (JSON.stringify(projects) !== JSON.stringify(newProjects)) {
                projects = newProjects;
                console.log("Projects updated", projects);

                // Initialize download options for each project
                const newOptions = {};
                Object.keys(newProjects).forEach((projectId) => {
                    newOptions[projectId] = {
                        transcript: true,
                        summary: true,
                        qualify: false,
                        draft: false,
                        review: false,
                    };
                });
                downloadOptions.set(newOptions);
            }
        } catch (e) {
            console.error("State update error:", e);
        }
    });

    const handleDownload = () => {
        downloadModalOpen = true;
    };

    let meeting_id = createLocalStorageStore(
        "realtime_meeting_id",
        crypto.randomUUID(),
    );

    const processDownload = async (format) => {
        // Handle the actual download logic here
        console.log(
            "Processing download with options:",
            $downloadOptions,
            "Full transcript:",
            fullTranscript,
            "Format:",
            format,
        );
        download_status = "Processing...";

        const request_id = crypto.randomUUID();

        await make_request(
            {
                type: "templating",
                sub_type: "download_realtime",
                request_id: request_id,
                payload: {
                    file_type: format,
                    meeting_id: $meeting_id,
                    full_transcript: fullTranscript,
                    projects: Object.keys(projects).map((project_id) => {
                        return {
                            project_id: project_id,
                            selected_components: Object.keys(
                                $downloadOptions[project_id],
                            ).filter(
                                (key) => $downloadOptions[project_id][key],
                            ),
                        };
                    }),
                },
            },
            ["realtime", "download"],
            {
                completed: ["realtime", "download", "data", "completed"],
                base64_file: ["realtime", "download", "data", "base64_file"],
                file_name: ["realtime", "download", "data", "file_name"],
                file_type: ["realtime", "download", "data", "file_type"],
                status: ["realtime", "download", "data", "status"],
            },
        );

        global_state.update((state) => {
            updateNestedDict(
                state,
                ["realtime", "download", "data", "status"],
                "",
            );
            return state;
        });
    };

    const resetDownloadStatus = () => {
        download_status = null;
        // Also clear the status from global state to prevent it from being restored
        global_state.update((state) => {
            updateNestedDict(
                state,
                ["realtime", "download", "data", "status"],
                "",
            );
            return state;
        });
    };

    onDestroy(() => {
        unsubscribe();
    });
</script>

<div class="w-full flex flex-grow flex-col justify-center mb-40">
    <div class="flex flex-col items-center mt-5">
        <Card size="xl">
            <div class="flex">
                {#if leftPanelVisible}
                    <div class="w-1/2 border-r border-gray-200">
                        <RealtimeTranscription />
                    </div>
                {/if}

                <!-- Toggle button -->
                <button
                    on:click={togglePanel}
                    class="p-2 bg-gray-200 hover:bg-purple-500 active:bg-purple-700"
                >
                    {#if leftPanelVisible}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    {:else}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    {/if}
                </button>

                <!-- Right Panel -->
                <div class={leftPanelVisible ? "w-1/2" : "w-full"}>
                    <div class="h-full p-4 overflow-y-auto">
                        <P size="lg" class="font-bold mb-10 mt-2">
                            <Span gradient>Projects</Span>
                        </P>
                        {#if Object.keys(projects).length > 0}
                            <CustomAccordion>
                                {#each Object.values(projects) as project}
                                    <AccordionItem open={true}>
                                        <span slot="header">
                                            {project.title}
                                        </span>
                                        <RealtimeAnalysis
                                            project_data={project}
                                        />
                                    </AccordionItem>
                                {/each}
                            </CustomAccordion>
                        {:else}
                            <p>No projects</p>
                        {/if}
                    </div>
                </div>
            </div>

            <div class="flex justify-end">
                <Button
                    class="mr-3 hover:bg-purple-200 rounded-full p-2 bg-transparent"
                    title="Clear Transcript & Projects"
                    on:click={() => {
                        modal_open = true;
                    }}
                >
                    <TrashBinOutline color="grey"></TrashBinOutline>
                </Button>

                <Button
                    size="sm"
                    color="purple"
                    on:click={() => handleDownload()}
                >
                    Download & Process
                </Button>
            </div>
        </Card>
    </div>
</div>

<!-- Download Modal -->
<Modal bind:open={downloadModalOpen} size="lg" autoclose={false} class="w-full">
    <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-semibold text-gray-900">Download & Process</h3>
    </div>

    <div class="space-y-6">
        {#each Object.entries(projects) as [projectId, project] (reactivityKey + projectId)}
            <div class="bg-gray-50 p-4 rounded-lg">
                <h4 class="text-lg font-medium text-gray-900 mb-3">
                    {project.title}
                </h4>
                <div class="flex flex-wrap gap-4">
                    <Checkbox
                        bind:checked={$downloadOptions[projectId].transcript}
                        class="text-sm"
                    >
                        Transcript
                    </Checkbox>
                    <Checkbox
                        bind:checked={$downloadOptions[projectId].summary}
                        class="text-sm"
                    >
                        Summary
                    </Checkbox>
                    <Checkbox
                        bind:checked={$downloadOptions[projectId].qualify}
                        class="text-sm"
                    >
                        Qualify
                    </Checkbox>
                    <Checkbox
                        bind:checked={$downloadOptions[projectId].draft}
                        class="text-sm"
                    >
                        Draft
                    </Checkbox>
                    <Checkbox
                        bind:checked={$downloadOptions[projectId].review}
                        class="text-sm"
                        disabled={!$downloadOptions[projectId]?.draft}
                    >
                        Review
                    </Checkbox>
                </div>
            </div>
        {/each}

        <div class="border-t pt-4">
            <Checkbox bind:checked={fullTranscript} class="text-sm font-medium">
                Full transcript
            </Checkbox>
        </div>
        <div class="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Note: Qualify, Draft and Review do not have your custom instructions
        </div>
    </div>

    <div class="flex justify-end mt-6">
        {#if download_status == null}
            <Button size="sm" color="purple"
                >Download & Process<ChevronDownOutline
                    class="w-6 h-6 ms-2 text-white dark:text-white"
                /></Button
            >
            <Dropdown>
                <DropdownItem
                    on:click={async () => {
                        processDownload("docx");
                    }}>Docx</DropdownItem
                >
                <DropdownItem
                    on:click={async () => {
                        processDownload("pdf");
                    }}>PDF</DropdownItem
                >
            </Dropdown>
        {:else}
            <div
                class="flex items-center justify-center px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
            >
                <Spinner size="sm" class="mr-2 w-4 h-4" />
                <Span
                    class="text-sm font-medium text-blue-700 dark:text-blue-300"
                >
                    Status: {download_status}
                </Span>
            </div>
            <Button
                size="xs"
                on:click={resetDownloadStatus}
                color="alternative"
                class="hover:bg-purple-200  mx-3"
                title="Stop Processing"
            >
                <StopOutline color="red"></StopOutline>
            </Button>
        {/if}
    </div>
</Modal>

<GenericConfirmModal
    bind:modal_open
    has_delete_narratives={false}
    has_delete_instructions={false}
    use_files={false}
    title="Clear Transcript & Projects"
    message="Are you sure you want to clear the transcript and all projects information?\nThis action cannot be undone."
    on_confirm={() => {
        global_state.update((state) => {
            deleteNestedDict(state, ["realtime", "transcription"]);
            deleteNestedDict(state, ["realtime", "router", "projects", "data"]);
            return state;
        });

        modal_open = false;
        meeting_id.set(crypto.randomUUID());
        window.location.reload();
    }}
></GenericConfirmModal>
